import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2,
  Building2,
  CheckCircle,
  AlertCircle,
  Mail,
  User,
  Eye,
  EyeOff,
  ArrowLeft,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/services/api';
import { useMarketingTranslations } from '@/hooks/useMarketingTranslations';
import { LanguageToggle } from '@/components/ui/LanguageToggle';
import { logError } from '@/utils/errorHandler';

interface InvitationDetails {
  token: string;
  org_request: {
    email: string;
    org_name: string;
    message: string;
    requested_at: string;
  };
  expires_at: string;
  status: string;
}

interface FormData {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
}

const CreateOrganizationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // Translation hooks
  const {
    getInvitationText,
    getInvitationNavigation,
    getInvitationBranding,
    getInvitationForm,
    getInvitationField,
    getInvitationButton,
    getInvitationSuccess,
    getInvitationTimeUnit,
  } = useMarketingTranslations();

  // State management
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  });

  const [formErrors, setFormErrors] = useState<Partial<FormData>>({});

  // Validate token and fetch invitation details
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No invitation token provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.get(`/api/orgs/org-invitations/${token}/`);

        if (response.data.status !== 'pending') {
          if (response.data.status === 'expired') {
            setError('This invitation has expired');
          } else if (response.data.status === 'accepted') {
            // Invitation already used - redirect to login with helpful message
            toast.info('This invitation has already been used');
            navigate('/login?message=invitation_already_used');
            return;
          } else {
            setError('This invitation is no longer valid');
          }
          setLoading(false);
          return;
        }

        // Check if invitation is expired
        const expiresAt = new Date(response.data.expires_at);
        const now = new Date();
        if (now > expiresAt) {
          setError('This invitation has expired');
          setLoading(false);
          return;
        }

        setInvitation(response.data);
        setError(null);
      } catch (err: any) {
        logError(err, {
          component: 'CreateOrganizationPage',
          action: 'validateInvitation',
          metadata: { token },
        });
        if (err.response?.status === 404) {
          setError('Invalid invitation token');
        } else {
          setError('Failed to validate invitation');
        }
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token, navigate]);

  // Form validation
  const validateForm = (): boolean => {
    const errors: Partial<FormData> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = getInvitationField('firstName', 'required');
    }

    if (!formData.lastName.trim()) {
      errors.lastName = getInvitationField('lastName', 'required');
    }

    if (!formData.password) {
      errors.password = getInvitationField('password', 'required');
    } else if (formData.password.length < 8) {
      errors.password = getInvitationField('password', 'minLength');
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = getInvitationField('password', 'complexity');
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = getInvitationField('confirmPassword', 'required');
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = getInvitationField('confirmPassword', 'mismatch');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(getInvitationForm('validation.fixErrors'));
      return;
    }

    if (!invitation || !token) {
      setError(getInvitationForm('errors.invalidState'));
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const response = await api.post(`/api/orgs/org-invitations/${token}/accept/`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        password: formData.password,
      });

      if (response.status === 200) {
        setSuccess(true);
        toast.success(getInvitationSuccess('subtitle'));

        setTimeout(() => {
          navigate('/login?message=organization_created');
        }, 3000);
      }
    } catch (err: any) {
      logError(err, {
        component: 'CreateOrganizationPage',
        action: 'createOrganization',
        metadata: { token, firstName: formData.firstName, lastName: formData.lastName },
      });

      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.status === 400) {
        setError(getInvitationForm('errors.invalidRequest'));
      } else if (err.response?.status === 404) {
        setError(getInvitationForm('errors.invitationNotFound'));
      } else {
        setError(getInvitationForm('errors.creationFailed'));
      }

      toast.error(getInvitationForm('errors.creationFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));

    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Calculate time remaining
  const getTimeRemaining = () => {
    if (!invitation) return '';

    const expiresAt = new Date(invitation.expires_at);
    const now = new Date();
    const diffMs = expiresAt.getTime() - now.getTime();

    if (diffMs <= 0) return getInvitationForm('expired');

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 24) {
      const diffDays = Math.floor(diffHours / 24);
      const unit =
        diffDays > 1 ? getInvitationTimeUnit('daysPlural') : getInvitationTimeUnit('days');
      return getInvitationForm('timeRemaining', { time: `${diffDays} ${unit}` });
    } else if (diffHours > 0) {
      const unit =
        diffHours > 1 ? getInvitationTimeUnit('hoursPlural') : getInvitationTimeUnit('hours');
      return getInvitationForm('timeRemaining', {
        time: `${diffHours} ${unit}`,
      });
    } else {
      const unit =
        diffMinutes > 1 ? getInvitationTimeUnit('minutesPlural') : getInvitationTimeUnit('minutes');
      return getInvitationForm('timeRemaining', {
        time: `${diffMinutes} ${unit}`,
      });
    }
  };

  // Loading state
  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Loader2 className="mx-auto mb-4 size-12 animate-spin text-primary-400" />
          <h2 className="mb-2 text-xl font-semibold text-white">Validating Invitation</h2>
          <p className="text-slate-300">Please wait while we verify your invitation...</p>
        </motion.div>
      </main>
    );
  }

  // Error state
  if (error && !invitation) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="border-slate-700/50 bg-slate-800/90 backdrop-blur-sm">
            <CardHeader className="pb-4 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="mx-auto mb-4 w-fit rounded-full bg-red-500/10 p-3"
              >
                <AlertCircle className="size-8 text-red-400" />
              </motion.div>
              <CardTitle className="text-xl text-white">
                {getInvitationText('error', 'title')}
              </CardTitle>
              <CardDescription className="mt-2 text-red-400">{error}</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-4">
                <Alert className="border-slate-600 bg-slate-700/50">
                  <AlertCircle className="size-4" />
                  <AlertDescription className="text-sm text-slate-300">
                    {getInvitationText('error', 'helpText')}
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  <Button
                    onClick={() => navigate('/login')}
                    className="w-full bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600"
                  >
                    {getInvitationText('error', 'buttons.goToLogin')}
                  </Button>
                  <Button onClick={() => navigate('/')} className="w-full" variant="outline">
                    {getInvitationText('error', 'buttons.returnHome')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    );
  }

  // Success state
  if (success) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="border-slate-700/50 bg-slate-800/90 backdrop-blur-sm">
            <CardHeader className="pb-4 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="mx-auto mb-4 w-fit rounded-full bg-green-500/10 p-3"
              >
                <CheckCircle className="size-8 text-green-400" />
              </motion.div>
              <CardTitle className="text-xl text-white">{getInvitationSuccess('title')}</CardTitle>
              <CardDescription className="mt-2 text-green-400">
                {getInvitationSuccess('subtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-4">
                <Alert className="border-slate-600 bg-slate-700/50">
                  <CheckCircle className="size-4" />
                  <AlertDescription className="text-sm text-slate-300">
                    {getInvitationSuccess('message')}
                  </AlertDescription>
                </Alert>
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600"
                >
                  {getInvitationSuccess('button')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    );
  }

  // Main form
  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <nav className="relative z-10 border-b border-slate-700/50 bg-slate-900/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="rounded-lg bg-primary-600 p-2">
                <Building2 className="size-6 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-white">
                  {getInvitationBranding('title')}
                </span>
                <p className="hidden text-xs text-slate-400 sm:block">
                  {getInvitationBranding('subtitle')}
                </p>
              </div>
            </Link>

            <div className="flex items-center space-x-4">
              <LanguageToggle />
              <Button
                asChild
                variant="outline"
                size="sm"
                className="border-slate-600 bg-slate-800/80 text-slate-300 hover:border-slate-500 hover:bg-slate-700/80 hover:text-white"
              >
                <Link to="/">
                  <ArrowLeft className="mr-2 size-4" />
                  {getInvitationNavigation('home')}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <motion.div
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="mb-4 text-4xl font-bold text-white">{getInvitationForm('title')}</h1>
            <p className="text-xl text-slate-300">
              {getInvitationForm('description', {
                orgName: invitation?.org_request?.org_name || '',
              })}
            </p>
          </motion.div>

          <div className="grid gap-12 lg:grid-cols-2">
            {/* Left Panel - Organization Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="space-y-6"
            >
              <Card className="border-slate-700/50 bg-slate-800/90 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center text-white">
                    <Building2 className="mr-2 size-5 text-primary-400" />
                    {invitation?.org_request?.org_name || 'Organization'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3 text-slate-300">
                    <p>
                      {getInvitationForm('joinOrg', {
                        orgName: invitation?.org_request?.org_name || '',
                      })}
                    </p>

                    <div className="space-y-2">
                      <div className="flex items-center text-sm">
                        <Mail className="mr-2 size-4 text-primary-400" />
                        <span>{invitation?.org_request?.email || 'Loading...'}</span>
                      </div>
                      <div className="flex items-center text-sm">
                        <User className="mr-2 size-4 text-primary-400" />
                        <span>{getInvitationForm('adminRole')}</span>
                      </div>
                    </div>

                    {/* Time remaining badge */}
                    <div className="flex justify-start">
                      <span className="inline-flex items-center rounded-full border border-orange-500/20 bg-orange-500/10 px-3 py-1 text-xs font-medium text-orange-400">
                        {getTimeRemaining()}
                      </span>
                    </div>

                    {invitation?.org_request?.message && (
                      <div className="mt-6 rounded-lg border border-slate-600 bg-slate-700/50 p-4">
                        <h4 className="mb-2 font-medium text-white">
                          {getInvitationForm('messageLabel')}
                        </h4>
                        <p className="text-sm italic text-slate-300">
                          "{invitation.org_request.message}"
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Right Panel - Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="border-slate-700/50 bg-slate-800/90 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-white">{getInvitationForm('subtitle')}</CardTitle>
                  <CardDescription className="text-slate-400">
                    {getInvitationForm('description', {
                      orgName: invitation?.org_request?.org_name || '',
                    })}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  {error && (
                    <Alert className="mb-6 border-red-500/50 bg-red-500/10" variant="destructive">
                      <AlertCircle className="size-4" />
                      <AlertDescription className="text-red-400">{error}</AlertDescription>
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Email (read-only) */}
                    <div>
                      <Label className="text-slate-200">
                        {getInvitationField('email', 'label')}
                      </Label>
                      <Input
                        type="email"
                        value={invitation?.org_request?.email || ''}
                        disabled
                        className="mt-2 border-slate-600 bg-slate-700/50 text-slate-300"
                      />
                      <p className="mt-1 text-xs text-slate-400">
                        {getInvitationField('email', 'readOnly')}
                      </p>
                    </div>

                    {/* First Name */}
                    <div>
                      <Label className="text-slate-200">
                        {getInvitationField('firstName', 'label')} *
                      </Label>
                      <Input
                        type="text"
                        value={formData.firstName}
                        onChange={handleInputChange('firstName')}
                        className={`mt-2 border-slate-600 bg-slate-800/50 text-white placeholder:text-slate-400 
                          ${formErrors.firstName ? 'border-red-500' : 'focus:border-primary-500'}`}
                        placeholder={getInvitationField('firstName', 'label')}
                      />
                      {formErrors.firstName && (
                        <p className="mt-1 text-sm text-red-400">{formErrors.firstName}</p>
                      )}
                    </div>

                    {/* Last Name */}
                    <div>
                      <Label className="text-slate-200">
                        {getInvitationField('lastName', 'label')} *
                      </Label>
                      <Input
                        type="text"
                        value={formData.lastName}
                        onChange={handleInputChange('lastName')}
                        className={`mt-2 border-slate-600 bg-slate-800/50 text-white placeholder:text-slate-400 
                          ${formErrors.lastName ? 'border-red-500' : 'focus:border-primary-500'}`}
                        placeholder={getInvitationField('lastName', 'label')}
                      />
                      {formErrors.lastName && (
                        <p className="mt-1 text-sm text-red-400">{formErrors.lastName}</p>
                      )}
                    </div>

                    {/* Password */}
                    <div>
                      <Label className="text-slate-200">
                        {getInvitationField('password', 'label')} *
                      </Label>
                      <div className="relative mt-2">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={handleInputChange('password')}
                          className={`border-slate-600 bg-slate-800/50 pr-10 text-white placeholder:text-slate-400 
                            ${formErrors.password ? 'border-red-500' : 'focus:border-primary-500'}`}
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                        >
                          {showPassword ? (
                            <EyeOff className="size-4" />
                          ) : (
                            <Eye className="size-4" />
                          )}
                        </button>
                      </div>
                      {formErrors.password && (
                        <p className="mt-1 text-sm text-red-400">{formErrors.password}</p>
                      )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <Label className="text-slate-200">
                        {getInvitationField('confirmPassword', 'label')} *
                      </Label>
                      <div className="relative mt-2">
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={formData.confirmPassword}
                          onChange={handleInputChange('confirmPassword')}
                          className={`border-slate-600 bg-slate-800/50 pr-10 text-white placeholder:text-slate-400 
                            ${formErrors.confirmPassword ? 'border-red-500' : 'focus:border-primary-500'}`}
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="size-4" />
                          ) : (
                            <Eye className="size-4" />
                          )}
                        </button>
                      </div>
                      {formErrors.confirmPassword && (
                        <p className="mt-1 text-sm text-red-400">{formErrors.confirmPassword}</p>
                      )}
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 disabled:opacity-50"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 size-4 animate-spin" />
                          {getInvitationButton('creating')}
                        </>
                      ) : (
                        getInvitationButton('createOrganization')
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default CreateOrganizationPage;
